/**
 * Component: Ajax Cart
 * ------------------------------------------------------------------------------
 * Create a mini cart with increase and decrease quantity function.
 *
 * @namespace ajaxCart
 */
import {formatMoney} from '@shopify/theme-currency';
import * as cart from '@shopify/theme-cart';
import {disableBodyScroll, enableBodyScroll} from 'body-scroll-lock';
import axios from 'axios';
// import {ajax} from 'jquery';

import {on} from '../helpers/utils';
import {misc as icons} from '../helpers/svg-map';
import cssClasses from '../helpers/cssClasses';

import {quantitySelector, quantitySelectorTemplate} from './quantity-selector';

/**
 * DOM selectors.
 */
const selectors = {
  container: '[js-ajax-cart="container"]',
  cart: '[js-ajax-cart="cart"]',
  open: '[js-ajax-cart="open"]',
  close: '[js-ajax-cart="close"]',
  quantity: '[js-ajax-cart="quantity"]',
  quantitySelector: '[js-cart="quantitySelector"]',
  itemList: '[js-ajax-cart="list"]',
  footer: '[js-ajax-cart="footer"]',
  totalPrice: '[js-ajax-cart="totalPrice"]',
  counter: '[js-ajax-cart="counter"]',
  lineItem: '[js-cart-table="lineItem"]',
};

/**
 * Export default AjaxCart.
 */
export default () => {
  const cartPage = window.location.pathname;

  /**
   * DOM node selectors.
   */
  const nodeSelectors = {
    body: document.body,
    container: document.querySelector(selectors.container),
    cart: document.querySelector(selectors.cart),
    open: [...document.querySelectorAll(selectors.open)],
    close: [...document.querySelectorAll(selectors.close)],
    quantity: [...document.querySelectorAll(selectors.quantity)],
    totalPrice: [...document.querySelectorAll(selectors.totalPrice)],
    counter: [...document.querySelectorAll(selectors.counter)],
    itemList: document.querySelector(selectors.itemList),
    footer: document.querySelector(selectors.footer),
    ajaxCartCount: document.getElementById('ajax-cart-count'),
    tools: document.querySelector('.cart-ajax__tools'),
    cartHeader: document.querySelector('.cart-ajax__header'),
    cartBody: document.querySelector('.cart-ajax__body'),
  };

  function init() {
    if (theme.ajaxCart.enable === false || cartPage === '/cart') {
      return false;
    }

    setBusListeners();
    setEventListeners();
  }

  function setBusListeners() {
    Karmoon.EventBus.listen(
      ['AddToCart:itemRemoved', 'AddToCart:itemAdded'],
      () => {
        getCartJson();
      },
    );

    Karmoon.EventBus.listen(
      ['AddToCart:isRemoving', 'AjaxCart:isUpdating'],
      () => {
        cartUpdating();
      },
    );

    Karmoon.EventBus.listen('AjaxCart:updated', (cartData) => {
      cartUpdated();
      handleOpenCart(cartData);
    });
  }

  function setEventListeners() {
    document.documentElement.addEventListener('keydown', handleKeydown);

    nodeSelectors.open.forEach((item) => {
      on('click', item, () => getCartJson());
    });

    nodeSelectors.close.forEach((item) => {
      on('click', item, () => cartClose());
    });
  }

  function handleOpenCart(cartData) {
    cartOpen();
    cartRender(cartData);
    cartCounter(cartData);
    cartCount(cartData);
    updateTotal(cartData);

    // const toolsHeight = nodeSelectors.tools.offsetHeight;
    // const headerHeight = nodeSelectors.cartHeader.offsetHeight;

    // if (toolsHeight && headerHeight) {
    //   const bodyHeight = `${toolsHeight + headerHeight}px`;
    //   const sscont = document.querySelector('.ss-container');
    //   sscont.style.height = `calc(100% - ${bodyHeight})`;
    // }
  }

  /**
   * Ajax call to get all cart data json.
   */
  function getCartJson() {
    axios
      .get(`/cart.js?no-cache}`)
      .then((response) => {
        Karmoon.EventBus.emit('AjaxCart:updated', response.data);
      })
      .catch((error) => {
        window.console.error(error);
        return error;
      });
  }

  /**
   * If key press is ESC close AjaxCart.
   */
  function handleKeydown(event) {
    if (event.keyCode === 27) {
      cartClose();
    }
  }

  /**
   * Handle quantity updates.
   * @param {HTMLElement} element - Quantity selector node element.
   */
  function handleQuantityEvent(element) {
    Karmoon.EventBus.emit('AjaxCart:isUpdating');

    const data = {
      index: element.closest(selectors.lineItem).dataset.index,
      key: element.closest(selectors.lineItem).dataset.key,
      quantity: parseInt(element.querySelector('input').value, 10),
    };

    cart
      .updateItem(data.key, {quantity: data.quantity})
      .then(() => {
        Karmoon.EventBus.emit('AddToCart:itemAdded');
      })
      .catch((error) => {
        if (typeof response === 'undefined') {
          return;
        }

        throw new Error('Could not update item data', error);
      });
  }

  // const htmlbody = document.querySelector('html');
  // const bodybody = document.querySelector('body');

  /**
   * Open Ajax Cart Open.
   */
  function cartOpen() {
    disableBodyScroll(nodeSelectors.body);
    nodeSelectors.body.classList.add('gorgias-back');
    Karmoon.EventBus.emit('AjaxCart:Open');
    nodeSelectors.container.classList.add(cssClasses.active);
    // htmlbody.classList.add('activeCart');

    // Remove Cart Count on Ajax open
    const cartCount = document.querySelectorAll('.cart-count');
    for (let i = 0; i < cartCount.length; ++i) {
      const item = cartCount[i];
      item.classList.add('hidden');
    }
  }

  /**
   * Close Ajax Cart Open.
   */
  function cartClose() {
    enableBodyScroll(nodeSelectors.body);
    nodeSelectors.body.classList.remove('gorgias-back');
    Karmoon.EventBus.emit('AjaxCart:Close');
    nodeSelectors.container.classList.remove(cssClasses.active);
    // htmlbody.classList.remove('activeCart');

    // Add Cart Count on Ajax close
    const cartCount = document.querySelectorAll('.cart-count');

    for (let i = 0; i < cartCount.length; ++i) {
      const item = cartCount[i];
      item.classList.remove('hidden');
    }
  }

  /**
   * Instantiate quantity selectors using id.
   * - Callback has a timer to reduce number of calls.
   */
  function constructQuantitySelectors() {
    const quantitySelectors = document.querySelectorAll(
      selectors.quantitySelector,
    );

    [...quantitySelectors].forEach((item) => {
      quantitySelector(`${item.id}`, () => {
        window.clearTimeout(window.timer);
        window.timer = window.setTimeout(() => {
          handleQuantityEvent(item);
        }, 250);
      }).init();
    });
  }

  /**
   * Build cart template
   * @param {Object} data - Cart data.
   */
  function cartRender(data) {
    const cartItems = data.items;
    const emptyCart = document.querySelector('.cart-ajax__empty');
    // console.log(cartItems);

    if (cartItems.length) {
      const cartTemplate = getCartTemplate(data);
      nodeSelectors.itemList.innerHTML = cartTemplate;
      nodeSelectors.footer.classList.add(cssClasses.active);

      document
        .querySelector('.cart-ajax__tools')
        .classList.remove('empty-cart');

      emptyCart.classList.add('inactive');
      emptyCart.classList.remove('active');
    } else {
      // const emptyCartTemplate = getEmptyCart(data);
      // nodeSelectors.itemList.innerHTML = emptyCartTemplate;

      emptyCart.classList.add('active');
      emptyCart.classList.remove('inactive');

      nodeSelectors.footer.classList.remove(cssClasses.active);

      document.querySelector('.cart-ajax__body').classList.add('empty-cart');
      document.querySelector('.cart-ajax__tools').classList.add('empty-cart');
    }

    constructQuantitySelectors();
  }

  /**
   * Get cart template
   * @param {Object} data - Cart data.
   * @returns {String}
   */
  function getCartTemplate(data) {
    const lineItemsTemplate = data.items
      .map((item, index) => getLineItemTemplate(item, index))
      .join('');

    return `${lineItemsTemplate}`;
  }

  /**
   * Return line item HTML block.
   * @param {Object} item - Cart item.
   * @param {interger} index - Index count.
   * @returns {HTMLElement}
   */
  function getLineItemTemplate(item, index) {
    const cartVariantTitle = getVariantTitle(item);
    const cartLinePrice = getLinePrice(item);
    const createQuantitySelector = quantitySelectorTemplate(item, index);
    // const isolate = getAcf(item);

    // console.log(item);

    const cartRow = `
          <div
            class="cart-ajax__row cart-data"
            data-key="${item.key}"
            data-index="${index}"
            js-cart-table="lineItem"
            >
            <div class="cart-ajax__row__column">
              <a href="${item.url}" title="Product Image" class="js-click-gtm cart-ajax__row__image" style="background-image: url(${item.image})"></a>
            </div>
            <div class="cart-ajax__row__column">
              <a href="${item.url}" title="${item.product_title}" class="js-click-gtm h6 cart-ajax__row__title">
                <small class="cart-ajax__row__variant cart-ajax__row__vendor">
                  ${item.vendor}
                </small>
                ${item.product_title}
                <small class="cart-ajax__row__variant">
                  ${cartVariantTitle}
                </small>
              </a>
            </div>
            <div class="cart-ajax__row__column cart-ajax__row-item-details">
              ${createQuantitySelector}
              <span class="cart-ajax__row__money">
                ${cartLinePrice}
              </span>
              <button
                js-cart="remove"
                data-item-key="${item.key}"
                name="Remove"
                type="button"
                class="cart-ajax__remove"
                tabindex="0">
                ${icons.remove}
              </button>
            </div>
          </div>`;

    return cartRow;
  }

  /**
   * Return Variant title if avaible.
   * @param {Object} item - Cart item.
   * @returns {HTMLElement}
   */
  function getVariantTitle(item) {
    return item.variant_title === null ? '' : item.variant_title;
  }

  /**
   * Return formatted line item price.
   * @param {Object} item - Cart item.
   * @returns {HTMLElement}
   */
  function getLinePrice(item) {
    const cartLinePrice = formatMoney(
      item.line_price,
      theme.moneyFormat,
    ).replace('.00', '');

    return cartLinePrice;
  }

  /**
   * Update total cart price.
   * @returns {HTMLElement}
   */
  // function getEmptyCart() {
  //   const emptyCart = `
  //       <div class="cart-ajax__empty">
  //         <h3>Your shopping bag is currently empty</h3>
  //         <a href="/collections/frontpage" title="Continue shopping" class="button">Continue Shopping</a>
  //       </div>
  //     `;

  //   return emptyCart;
  // }

  /**
   * Update total items in cart counter.
   * @param {Object} data - Cart data.
   */
  function cartCounter(data) {
    [...nodeSelectors.counter].forEach((item) => {
      item.innerHTML = data.item_count;

      if (data.item_count === 0) {
        item.classList.remove(cssClasses.active);
      } else {
        item.classList.add(cssClasses.active);
      }
    });
  }

  function cartCount(data) {
    const ajaxCount = nodeSelectors.ajaxCartCount;
    const cartItems = data.items;

    const countMap = cartItems.map((item) => {
      return item.quantity;
    });

    const sum = countMap.reduce(add, 0);

    function add(accumulator, a) {
      return accumulator + a;
    }

    let countText = '';
    if (sum === 0) {
      countText = '0 items';
    } else if (sum === 1) {
      countText = `${sum} item`;
    } else {
      countText = `${sum} items`;
    }

    ajaxCount.innerHTML = countText;
  }

  /**
   * Update total cart price.
   * @param {Object} data - Cart data.
   */
  function updateTotal(data) {
    const cartTotal = formatMoney(data.total_price, theme.moneyFormat).replace(
      '.00',
      '',
    );

    [...nodeSelectors.totalPrice].forEach((item) => {
      item.innerHTML = `<span class=money>${cartTotal}</span>`;
    });
  }

  // Add loading state when adding to cart.
  function cartUpdating() {
    nodeSelectors.container.classList.add(cssClasses.loading);
  }

  // Remove loading state from Ajax cart.
  function cartUpdated() {
    nodeSelectors.container.classList.remove(cssClasses.loading);
  }

  // Return immutable object.
  return Object.freeze({
    init,
  });
};
